import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PROVIDER_ICONS } from '@/components/auth/ProviderIcons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { describeAuthError, describeOAuthError } from '@/lib/authErrors';
import { linkErrorKey, oauthErrorKey } from '@/lib/authLink';
import { oauthProviders } from '@/lib/config';
import { markOAuthStart, usePartnersAuth } from '@partners/auth/PartnersAuth';
import { Notice, PartnersMark } from '@partners/components/Shell';
import { LocaleToggle } from '@/components/LocaleToggle';

/** Where every emailed link and OAuth round trip comes back (PKCE: exchanged by PartnersAuthProvider). */
export const SIGNIN_PATH = '/signin';

type Mode = { kind: 'password' } | { kind: 'link' } | { kind: 'sent'; email: string };

/**
 * Sign in: password, an emailed link (with its code as a fallback for another browser), Google, Apple. No
 * sign-up wording: an invitation is the only way in, and the claim (Gate) is what binds it. "Forgot password"
 * is the link itself (no reliance on GoTrue's recovery template).
 */
export function SignIn() {
  const { t } = useTranslation();
  const { client, session, ready, linkError, clearLinkError } = usePartnersAuth();
  const [mode, setMode] = React.useState<Mode>({ kind: 'password' });
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const providers = oauthProviders();

  if (ready && session) return <Navigate to="/" replace />;

  const authError = (key: string) => t(`promoter.auth.errors.${key}`);
  const normalized = () => email.trim().toLowerCase();

  async function onPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await client.auth.signInWithPassword({ email: normalized(), password });
    setBusy(false);
    if (err) {
      console.warn('sign-in refused', err);
      setError(authError(describeAuthError(err) ?? 'generic'));
    }
  }

  async function onSendLink(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const address = normalized();
    const { error: err } = await client.auth.signInWithOtp({
      email: address,
      // An invited promoter may have no account yet: the link creates it; nothing is granted until the claim.
      options: { emailRedirectTo: window.location.origin + SIGNIN_PATH, shouldCreateUser: true },
    });
    setBusy(false);
    if (err) {
      console.warn('sign-in link not sent', err);
      setError(authError(describeAuthError(err) ?? 'generic'));
      return;
    }
    clearLinkError();
    setMode({ kind: 'sent', email: address });
  }

  async function onVerifyCode(event: React.FormEvent, address: string) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await client.auth.verifyOtp({ email: address, token: code.trim(), type: 'email' });
    setBusy(false);
    if (err) {
      console.warn('sign-in code refused', err);
      const key = describeAuthError(err);
      setError(authError(key === 'invalidCredentials' || key === 'generic' || !key ? 'linkExpired' : key));
    }
  }

  async function onProvider(provider: 'google' | 'apple') {
    setError(null);
    markOAuthStart(provider);
    const { error: err } = await client.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin + SIGNIN_PATH,
        ...(provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {}),
      },
    });
    if (err) {
      console.warn('oauth sign-in not started', err);
      setError(authError(describeOAuthError(err)));
    }
  }

  const linkMessage = linkError
    ? authError(linkError.via === 'oauth' ? oauthErrorKey(linkError) : linkErrorKey(linkError.code))
    : null;
  const offered = (['google', 'apple'] as const).filter((p) => providers[p]);

  return (
    <div className="grid min-h-full place-items-center bg-canvas px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between gap-3">
          <PartnersMark />
          <LocaleToggle />
        </div>
        <Card>
          <CardContent className="flex flex-col gap-5 p-6 sm:p-8">
            {mode.kind === 'sent' ? (
              <>
                <h1 className="text-2xl font-semibold text-ink">{t('promoter.auth.link.sentTitle')}</h1>
                <p className="text-base text-ink-secondary">{t('promoter.auth.link.sent', { email: mode.email })}</p>
                <Notice>{error}</Notice>
                <form className="flex flex-col gap-3" onSubmit={(e) => void onVerifyCode(e, mode.email)}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="partners-code">{t('promoter.auth.link.code')}</Label>
                    <Input
                      id="partners-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={busy || code.trim() === ''}>
                    {busy ? t('promoter.auth.link.verifying') : t('promoter.auth.link.verify')}
                  </Button>
                </form>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setError(null);
                    setCode('');
                    setMode({ kind: 'link' });
                  }}
                >
                  {t('promoter.auth.link.otherEmail')}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <h1 className="text-2xl font-semibold text-ink">
                    {mode.kind === 'password' ? t('promoter.auth.title') : t('promoter.auth.link.title')}
                  </h1>
                  <p className="mt-1 text-base text-ink-muted">
                    {mode.kind === 'password' ? t('promoter.auth.subtitle') : t('promoter.auth.link.body')}
                  </p>
                </div>
                <Notice>{linkMessage}</Notice>
                <Notice>{error}</Notice>

                {offered.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {offered.map((provider) => {
                      const Icon = PROVIDER_ICONS[provider];
                      return (
                        <Button key={provider} type="button" variant="outline" onClick={() => void onProvider(provider)}>
                          <Icon className="size-5" />
                          {t(`promoter.auth.${provider}`)}
                        </Button>
                      );
                    })}
                    <div className="flex items-center gap-3 text-sm text-ink-muted" aria-hidden>
                      <span className="h-px flex-1 bg-hairline" />
                      {t('promoter.auth.or')}
                      <span className="h-px flex-1 bg-hairline" />
                    </div>
                  </div>
                )}

                <form className="flex flex-col gap-4" onSubmit={(e) => void (mode.kind === 'password' ? onPassword(e) : onSendLink(e))}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="partners-email">{t('promoter.auth.email')}</Label>
                    <Input
                      id="partners-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  {mode.kind === 'password' && (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="partners-password">{t('promoter.auth.password')}</Label>
                      <Input
                        id="partners-password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  <Button type="submit" disabled={busy}>
                    {mode.kind === 'password'
                      ? busy
                        ? t('promoter.auth.signingIn')
                        : t('promoter.auth.signIn')
                      : busy
                        ? t('promoter.auth.link.sending')
                        : t('promoter.auth.link.send')}
                  </Button>
                </form>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setError(null);
                    setMode(mode.kind === 'password' ? { kind: 'link' } : { kind: 'password' });
                  }}
                >
                  {mode.kind === 'password' ? t('promoter.auth.useLink') : t('promoter.auth.usePassword')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
